/**
 * UserMappingSettings — /admin/user-mapping
 *
 * A stand-alone admin page that surfaces AND lets you edit the
 * reports_to / visible_to_dashboards mapping between agents, HODs,
 * CEO and Owner. Deliberately isolated from the existing HOD/CEO
 * dashboards — this page reads and writes only the additive fields on
 * `users`. No other endpoint is affected. See
 * routes/user_mapping_routes.py for the backend contract.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Users, Crown, Shield, User, ChevronRight, ChevronDown,
  RefreshCw, Search, MapPin, Layers, AlertCircle, GitBranch,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_TONES = {
  super_admin:   'bg-red-100 text-red-700',
  owner:         'bg-amber-100 text-amber-700',
  account_owner: 'bg-amber-100 text-amber-700',
  ceo:           'bg-rose-100 text-rose-700',
  hod:           'bg-purple-100 text-purple-700',
  hod_sales:     'bg-blue-100 text-blue-700',
  hod_marketing: 'bg-violet-100 text-violet-700',
  sales_agent:   'bg-green-100 text-green-700',
  sales_rep:     'bg-green-100 text-green-700',
  agent:         'bg-green-100 text-green-700',
  marketing:     'bg-pink-100 text-pink-700',
};
const roleTone = (r) => ROLE_TONES[r] || 'bg-slate-100 text-slate-700';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  owner: 'Owner', account_owner: 'Account Owner',
  ceo: 'CEO', hod: 'HOD', hod_sales: 'HOD Sales', hod_marketing: 'HOD Marketing',
  sales_agent: 'Sales Agent', sales_rep: 'Sales Rep', sales_manager: 'Sales Manager',
  agent: 'Agent', marketing: 'Marketing', customer_relations_agent: 'CR Agent',
  bdm: 'BDM', indirect_sales: 'Indirect Sales',
};
const roleLabel = (r) => ROLE_LABELS[r] || r || '—';

export default function UserMappingSettings() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [trace, setTrace] = useState(null);
  const [form, setForm] = useState({
    reports_to: '',
    visible_to_dashboards: [],
  });
  const [saving, setSaving] = useState(false);

  const fetchTree = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/user-mapping`, {
        params: { token },
      });
      setTree(r.data?.data || null);
      // Default: expand every HOD group so the tree is legible at first glance.
      const openAll = {};
      (r.data?.data?.hods || []).forEach((h) => { openAll[h.id] = true; });
      setExpanded(openAll);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load user mapping');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  // Prefer the new admins[] array; fall back to the single-slot
  // super_admin for backwards compat with an older backend build. If
  // both are present admins[] wins (it's the authoritative list).
  const admins = useMemo(() => {
    if (!tree) return [];
    if (Array.isArray(tree.admins) && tree.admins.length > 0) return tree.admins;
    return tree.super_admin ? [tree.super_admin] : [];
  }, [tree]);

  // Flat list of all users on the tree for the search + dropdowns.
  const allUsers = useMemo(() => {
    if (!tree) return [];
    const out = [];
    admins.forEach((a) => out.push(a));
    if (tree.ceo) out.push(tree.ceo);
    (tree.hods || []).forEach((h) => {
      out.push(h);
      (h.reports || []).forEach((a) => out.push(a));
    });
    (tree.lead_dashboard?.reports || []).forEach((a) => out.push(a));
    (tree.unassigned || []).forEach((a) => out.push(a));
    return out;
  }, [tree, admins]);

  const hods = useMemo(() => (tree?.hods || []), [tree]);
  const selected = useMemo(
    () => allUsers.find((u) => u.id === selectedId),
    [allUsers, selectedId],
  );

  // When a user is selected, seed the form + fetch their trace.
  useEffect(() => {
    if (!selected) { setForm({ reports_to: '', visible_to_dashboards: [] }); setTrace(null); return; }
    setForm({
      reports_to: selected.reports_to || '',
      visible_to_dashboards: selected.visible_to_dashboards || [],
    });
    axios
      .get(`${API}/api/admin/user-mapping/${selected.id}/trace`, { params: { token } })
      .then((r) => setTrace(r.data?.data || null))
      .catch(() => setTrace(null));
  }, [selected, token]);

  const filteredHods = useMemo(() => {
    if (!search.trim()) return hods;
    const q = search.trim().toLowerCase();
    return hods.map((h) => ({
      ...h,
      reports: (h.reports || []).filter((a) =>
        (a.full_name || '').toLowerCase().includes(q)
        || (a.email || '').toLowerCase().includes(q)
        || (a.role || '').toLowerCase().includes(q),
      ),
    })).filter((h) =>
      (h.full_name || '').toLowerCase().includes(q)
      || (h.reports || []).length > 0
    );
  }, [hods, search]);

  const toggleHod = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/admin/user-mapping/${selected.id}?token=${token}`,
        {
          reports_to: form.reports_to || null,
          visible_to_dashboards: form.visible_to_dashboards || [],
        },
      );
      toast.success(`${selected.full_name || 'User'} mapping saved`);
      await fetchTree();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = (hodId) => {
    setForm((prev) => {
      const cur = new Set(prev.visible_to_dashboards || []);
      if (cur.has(hodId)) cur.delete(hodId); else cur.add(hodId);
      return { ...prev, visible_to_dashboards: [...cur] };
    });
  };

  // Access gate — same audience as Menu Management.
  if (!user || !['super_admin', 'admin', 'account_owner', 'owner'].includes(user.role)) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-500">User Mapping is admin-only.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="user-mapping-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center">
          <GitBranch className="h-7 w-7" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">User Mapping Settings</h1>
          <p className="text-sm text-slate-500 mt-1">
            Map who reports to whom. Configures HOD → agent visibility across the CEO,
            HOD Sales, HOD Marketing and individual My Dashboards. Existing dashboards keep
            working with today's role/department heuristic until you set an explicit mapping here.
          </p>
        </div>
        <Button variant="outline" onClick={fetchTree} disabled={loading}>
          <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Totals strip */}
      {tree && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatChip label="HODs" value={tree.totals?.hods ?? 0} tone="purple" />
          <StatChip label="Agents" value={tree.totals?.agents ?? 0} tone="green" />
          <StatChip label="Lead Dashboard" value={tree.totals?.lead_dashboard ?? 0} tone="indigo" />
          <StatChip label="Explicit mapping" value={tree.totals?.matched ?? 0} tone="blue" />
          <StatChip label="Heuristic fallback" value={tree.totals?.heuristic ?? 0} tone="amber" />
          <StatChip label="Unassigned" value={tree.totals?.unassigned ?? 0} tone="rose" />
        </div>
      )}

      {/* Org tree — full-width now. The mapping editor previously sat
          in a right column that ate ~40% of the viewport even when no
          one was selected; it's now a popup dialog that opens on click. */}
      <div>
        <div>
          <Card>
            <CardHeader className="border-b">
              <div className="flex items-center gap-3">
                <CardTitle className="text-lg flex-1 flex items-center gap-2">
                  <Users size={20} /> Organization Tree
                </CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    placeholder="Search name / email / role"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 h-8 text-sm"
                  />
                </div>
              </div>
              <CardDescription>
                Click any user to open the Mapping Editor.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* Administrators — every super_admin + admin lives at
                  the top of the tree. Previously we only picked the
                  first super_admin which dropped Treppan Admin and
                  other real system operators. Small uppercase label
                  above the group so it doesn't visually merge with
                  the CEO band. */}
              {admins.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 px-1">
                    Administrators
                  </div>
                  {admins.map((a) => (
                    <UserRow
                      key={a.id}
                      user={a}
                      icon={Crown}
                      tone={roleTone(a.role)}
                      selected={selectedId === a.id}
                      onClick={() => setSelectedId(a.id)}
                      indent={0}
                    />
                  ))}
                </div>
              )}
              {/* CEO */}
              {tree?.ceo && (
                <UserRow user={tree.ceo} icon={Crown} tone={roleTone('ceo')}
                         selected={selectedId === tree.ceo.id}
                         onClick={() => setSelectedId(tree.ceo.id)} indent={1} />
              )}
              {/* HODs */}
              {filteredHods.map((h) => (
                <div key={h.id} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleHod(h.id)}
                      className="text-slate-400 hover:text-slate-700"
                      title={expanded[h.id] ? 'Collapse' : 'Expand'}
                    >
                      {expanded[h.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    <div className="flex-1">
                      <UserRow user={h} icon={Shield} tone={roleTone(h.role)}
                               selected={selectedId === h.id}
                               onClick={() => setSelectedId(h.id)}
                               indent={2}
                               suffix={`${(h.reports || []).length} agent(s)`} />
                    </div>
                  </div>
                  {expanded[h.id] && (h.reports || []).length > 0 && (
                    <div className="ml-6 space-y-2">
                      {h.reports.map((a) => (
                        <UserRow key={a.id} user={a} icon={User} tone={roleTone(a.role)}
                                 selected={selectedId === a.id}
                                 onClick={() => setSelectedId(a.id)} indent={4}
                                 badge={a._heuristic ? 'Heuristic' : null} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {/* Lead Dashboard bucket — BDM + indirect_sales users
                  that own the Brokerage Hub / Lead Dashboard flow
                  directly (no HOD in the reporting chain). Rendered
                  as a virtual group between HODs and Unassigned. */}
              {(tree?.lead_dashboard?.reports || []).length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-1">
                    <Layers size={16} className="text-indigo-500" />
                    {tree.lead_dashboard.label || 'Lead Dashboard'}
                    <Badge variant="outline" className="ml-1 text-xs">
                      {tree.lead_dashboard.reports.length}
                    </Badge>
                  </h3>
                  {tree.lead_dashboard.description && (
                    <p className="text-xs text-slate-500 mb-3">
                      {tree.lead_dashboard.description}
                    </p>
                  )}
                  <div className="space-y-2">
                    {tree.lead_dashboard.reports.map((a) => (
                      <UserRow key={a.id} user={a} icon={User} tone={roleTone(a.role)}
                               selected={selectedId === a.id}
                               onClick={() => setSelectedId(a.id)} indent={0}
                               badge={a._heuristic ? 'Heuristic' : null} />
                    ))}
                  </div>
                </div>
              )}

              {/* Unassigned */}
              {(tree?.unassigned || []).length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
                    <AlertCircle size={16} className="text-rose-500" />
                    Unassigned ({tree.unassigned.length}) — pick a manager on the right
                  </h3>
                  <div className="space-y-2">
                    {(tree.unassigned || []).map((a) => (
                      <UserRow key={a.id} user={a} icon={User} tone={roleTone(a.role)}
                               selected={selectedId === a.id}
                               onClick={() => setSelectedId(a.id)} indent={0} />
                    ))}
                  </div>
                </div>
              )}
              {loading && (
                <div className="text-center py-8 text-slate-500">Loading org tree…</div>
              )}
              {!loading && !tree && (
                <div className="text-center py-8 text-slate-500">No data.</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mapping Editor — was a right-column card that sat empty most
          of the time; now a focused popup opened by clicking any user
          in the tree. Uses the same capsule pattern as the CEO/HOD
          Assign Task dialog for visual consistency. */}
      <Dialog
        open={!!selected}
        onOpenChange={(open) => { if (!open) setSelectedId(null); }}
      >
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-violet-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                <MapPin size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-lg font-semibold text-slate-900">
                  Mapping Editor
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-0.5">
                  {selected
                    ? `Editing ${selected.full_name || selected.email || 'user'}`
                    : 'Pick a user in the tree'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {selected && (
            <>
              <div className="px-6 py-5 space-y-5 max-h-[68vh] overflow-y-auto">
                {/* Identity card */}
                <div className="p-4 rounded-lg border bg-slate-50/60">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${roleTone(selected.role)}`}>
                      <User size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 truncate">{selected.full_name}</div>
                      <div className="text-xs text-slate-500 truncate">{selected.email}</div>
                    </div>
                    <span className="shrink-0 rounded-md bg-slate-100 text-slate-600 text-[10.5px] font-medium tracking-wide px-2 py-0.5">
                      {roleLabel(selected.role)}
                    </span>
                  </div>
                </div>

                {/* Reports to */}
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    Reports to (primary HOD)
                  </Label>
                  <Select
                    value={form.reports_to || 'none'}
                    onValueChange={(v) => setForm((p) => ({ ...p, reports_to: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Pick a manager…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— none —</SelectItem>
                      {hods.filter((h) => h.id !== selected.id).map((h) => (
                        <SelectItem key={h.id} value={h.id}>
                          {h.full_name} · {roleLabel(h.role)}
                        </SelectItem>
                      ))}
                      {tree?.ceo && tree.ceo.id !== selected.id && (
                        <SelectItem value={tree.ceo.id}>
                          {tree.ceo.full_name} · CEO
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-400">
                    Sets who this person primarily rolls up to. Their KPIs feed that dashboard.
                  </p>
                </div>

                {/* Extra visibility */}
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    Extra dashboards (cross-functional)
                  </Label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-52 overflow-y-auto bg-white">
                    {hods.filter((h) => h.id !== selected.id).map((h) => {
                      const checked = form.visible_to_dashboards.includes(h.id);
                      return (
                        <label key={h.id} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleVisible(h.id)}
                            className="h-4 w-4 accent-violet-600"
                          />
                          <span className="text-sm text-slate-700 flex-1">
                            {h.full_name}
                            <span className="ml-2 rounded-md bg-slate-100 text-slate-600 text-[10.5px] font-medium tracking-wide px-2 py-0.5">
                              {roleLabel(h.role)}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                    {hods.length === 0 && (
                      <p className="text-xs text-slate-500">No HODs to pick from yet.</p>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Escape hatch — the user will also appear on these HODs' dashboards.
                  </p>
                </div>

                {/* Trace strip */}
                {trace && (
                  <div className="pt-4 border-t">
                    <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                      <Layers size={16} /> Trace visibility
                    </h4>
                    <div className="flex flex-wrap items-center gap-1 text-xs">
                      {(trace.chain || []).map((n, i) => (
                        <React.Fragment key={n.id}>
                          <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                            {n.name}
                            <span className="text-slate-400 ml-1">· {roleLabel(n.role)}</span>
                          </span>
                          {i < trace.chain.length - 1 && (
                            <ChevronRight size={12} className="text-slate-400" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    {(trace.extras || []).length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs text-slate-500 mb-1">Also visible to:</div>
                        <div className="flex flex-wrap gap-1">
                          {trace.extras.map((n) => (
                            <span key={n.id} className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-xs">
                              {n.dashboard || roleLabel(n.role)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <DialogFooter className="px-6 py-4 border-t bg-slate-50/70 gap-2">
                <Button variant="outline" onClick={() => setSelectedId(null)} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
                >
                  {saving ? (
                    <><RefreshCw size={14} className="mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <>Save mapping</>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------- small helpers ----------------------

function StatChip({ label, value, tone }) {
  const tones = {
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    green:  'bg-green-50 text-green-700 border-green-200',
    blue:   'bg-blue-50 text-blue-700 border-blue-200',
    amber:  'bg-amber-50 text-amber-700 border-amber-200',
    rose:   'bg-rose-50 text-rose-700 border-rose-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  };
  return (
    <div className={`px-4 py-3 rounded-lg border ${tones[tone] || ''}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs uppercase tracking-wide">{label}</div>
    </div>
  );
}

function UserRow({ user, icon: Icon = User, tone = '', selected, onClick, indent = 0, suffix = null, badge = null }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center gap-3 border ${
        selected
          ? 'border-blue-300 bg-blue-50'
          : 'border-transparent hover:bg-slate-50 hover:border-slate-100'
      }`}
      style={{ marginLeft: indent * 8 }}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${tone}`}>
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-800 truncate">
          {user.full_name || '(no name)'}
        </div>
        <div className="text-xs text-slate-500 truncate">{user.email}</div>
      </div>
      {/* Role as a quiet chip — no border, small caps, muted background.
          Reads as metadata rather than a call-to-action. */}
      <span className="shrink-0 rounded-md bg-slate-100 text-slate-600 text-[10.5px] font-medium tracking-wide px-2 py-0.5">
        {roleLabel(user.role)}
      </span>
      {/* Heuristic mapping — was an amber pill on every agent that read
          as a warning. Now a subtle Sparkles icon with a tooltip; the
          shape signals "auto-linked" without hijacking the row. */}
      {badge === 'Heuristic' && (
        <span
          className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-50 text-amber-600"
          title="Report-to link inferred from role — not explicitly set. Save an explicit HOD on the right to confirm."
          aria-label="Auto-linked report-to"
        >
          <Sparkles size={11} />
        </span>
      )}
      {/* Any other custom badge falls back to the old outline treatment. */}
      {badge && badge !== 'Heuristic' && (
        <Badge variant="outline" className="text-xs shrink-0">
          {badge}
        </Badge>
      )}
      {suffix && (
        <span className="text-xs text-slate-500 shrink-0">{suffix}</span>
      )}
    </button>
  );
}
